import * as React from 'react';
import type { User, UserManager } from 'oidc-client-ts';
import type {
  AuthConfig,
  AuthSession,
  AuthStatus,
  HydratedAuthSession,
  SignInOptions,
} from './types';
import { createUserManager, isOidcUserExpired, toSession } from './oidc';
import { IdentityMeError } from './session';
import { setActiveSession } from './token-source';

const AUTH_RETURN_STATE_KIND = 'cguard.auth.return';

interface AuthContextValue {
  status: AuthStatus;
  signIn: (options?: SignInOptions) => Promise<void>;
  signOut: () => Promise<void>;
  /** Force a silent refresh; resolves to the new session or null. */
  refresh: () => Promise<AuthSession | null>;
}

const AuthContext = React.createContext<AuthContextValue | null>(null);
const FAKE_SESSION_EXPIRY_SKEW_MS = 5_000;

export interface AuthProviderProps {
  config: AuthConfig;
  /** Used in tests / Storybook to bypass real OIDC and inject a session. */
  fakeSession?: AuthSession;
  /** Called when a fake-session surface explicitly asks to enter real sign-in. */
  onFakeSessionSignIn?: () => void | Promise<void>;
  children: React.ReactNode;
}

/**
 * Returns true if the current URL carries OIDC Authorization Code Flow
 * callback parameters (`code` + `state`, optionally `iss`, `session_state`).
 * Used to short-circuit AuthProvider's initial getUser() call into a
 * `signinRedirectCallback()` token exchange.
 */
function hasOidcCallbackParams(): boolean {
  if (typeof window === 'undefined') return false;
  const params = new URLSearchParams(window.location.search);
  return params.has('code') && params.has('state');
}

function sanitiseSameOriginPath(value: string): string | null {
  if (typeof window === 'undefined') return null;
  try {
    const url = new URL(value, window.location.origin);
    if (url.origin !== window.location.origin) return null;
    return `${url.pathname}${url.search}${url.hash}` || '/';
  } catch {
    return null;
  }
}

function getCurrentReturnPath(redirectUri: string): string {
  if (typeof window === 'undefined') return '/';
  const current = `${window.location.pathname}${window.location.search}${window.location.hash}`;
  const redirect = new URL(redirectUri, window.location.origin);
  const redirectPath = `${redirect.pathname}${redirect.search}${redirect.hash}`;
  if (current === redirectPath || current.startsWith(`${redirectPath}?`)) return '/';
  return sanitiseSameOriginPath(current) ?? '/';
}

function getReturnPathFromState(state: unknown): string | null {
  if (state === null || typeof state !== 'object') return null;
  if (!('kind' in state) || state.kind !== AUTH_RETURN_STATE_KIND) return null;
  if (!('returnPath' in state) || typeof state.returnPath !== 'string') return null;
  return sanitiseSameOriginPath(state.returnPath);
}

/**
 * Strip the OIDC callback params from the URL after a successful exchange
 * so a page reload doesn't re-attempt the (now consumed) authorization code.
 */
function clearOidcCallbackParams(returnPath?: string | null): void {
  if (typeof window === 'undefined') return;
  const nextUrl = returnPath ?? '/';
  window.history.replaceState({}, document.title, nextUrl);
  const event =
    typeof PopStateEvent === 'function'
      ? new PopStateEvent('popstate', { state: window.history.state })
      : new Event('popstate');
  window.dispatchEvent(event);
}

interface OidcCallbackExchange {
  key: string;
  promise: Promise<string | null>;
}

let activeOidcCallbackExchange: OidcCallbackExchange | null = null;

async function statusFromOidcUser(user: User | null): Promise<AuthStatus> {
  if (user === null) return { state: 'unauthenticated' };
  if (isOidcUserExpired(user)) return { state: 'unauthenticated', reason: 'expired' };

  try {
    const session = await toSession(user);
    return session === null
      ? { state: 'unauthenticated', reason: 'expired' }
      : { state: 'authenticated', session };
  } catch (error: unknown) {
    if (error instanceof IdentityMeError && error.kind === 'unauthorized') {
      return { state: 'unauthenticated' };
    }
    const authError = error instanceof Error ? error : new Error(String(error));
    return error instanceof IdentityMeError && error.kind === 'forbidden'
      ? { state: 'forbidden', error: authError }
      : { state: 'error', error: authError };
  }
}

function getOidcCallbackExchangeKey(config: AuthConfig): string | null {
  if (!hasOidcCallbackParams()) return null;
  return [config.authority, config.clientId, config.redirectUri, window.location.search].join('\n');
}

function exchangeOidcCallbackOnce({
  config,
  userManager,
}: {
  config: AuthConfig;
  userManager: UserManager;
}): Promise<string | null> {
  const key = getOidcCallbackExchangeKey(config);
  if (!key) return Promise.resolve(null);
  if (activeOidcCallbackExchange?.key === key) return activeOidcCallbackExchange.promise;

  const promise = userManager.signinRedirectCallback().then((user) => {
    const returnPath = getReturnPathFromState(user.state);
    clearOidcCallbackParams(returnPath);
    return returnPath;
  });
  activeOidcCallbackExchange = { key, promise };

  const clearActiveExchange = () => {
    if (activeOidcCallbackExchange?.key === key && activeOidcCallbackExchange.promise === promise) {
      activeOidcCallbackExchange = null;
    }
  };
  promise.then(clearActiveExchange, clearActiveExchange);

  return promise;
}

export function AuthProvider({
  config,
  fakeSession,
  onFakeSessionSignIn,
  children,
}: AuthProviderProps): React.JSX.Element {
  const hydratedFakeSession = fakeSession ? normaliseFakeSession(fakeSession) : null;
  const configRef = React.useRef(config);
  const userManagerRef = React.useRef<UserManager | null>(null);
  const explicitlySignedOutRef = React.useRef(false);
  const authTransitionRef = React.useRef(0);
  if (!userManagerRef.current && !fakeSession) {
    configRef.current = config;
    userManagerRef.current = createUserManager(config);
  }

  const [status, setStatus] = React.useState<AuthStatus>(() => {
    if (hydratedFakeSession) {
      if (isExpired(hydratedFakeSession)) {
        setActiveSession(null);
        return { state: 'unauthenticated', reason: 'expired' };
      }
      // Mirror the session into the imperative token source so that any code
      // path which configures the API client at module import time (e.g.
      // `main.tsx` calling `configureCguardClient({ getAccessToken: ... })`
      // before the AuthProvider renders) still sees the dev/storybook
      // fake token on the first request.
      setActiveSession(hydratedFakeSession);
      return { state: 'authenticated', session: hydratedFakeSession };
    }
    // A remounted provider must not expose a process-global bearer while its
    // fresh `/me` projection is still pending or ultimately fails.
    setActiveSession(null);
    return { state: 'loading' };
  });

  React.useEffect(() => {
    if (fakeSession) {
      authTransitionRef.current += 1;
      const session = normaliseFakeSession(fakeSession);
      if (isExpired(session)) {
        setActiveSession(null);
        setStatus({ state: 'unauthenticated', reason: 'expired' });
        return;
      }
      explicitlySignedOutRef.current = false;
      setActiveSession(session);
      setStatus({ state: 'authenticated', session });
      const timeoutId = window.setTimeout(() => {
        if (explicitlySignedOutRef.current) return;
        authTransitionRef.current += 1;
        setActiveSession(null);
        setStatus({ state: 'unauthenticated', reason: 'expired' });
      }, fakeSessionExpiryDelayMs(session));
      return () => window.clearTimeout(timeoutId);
    }
    const um = userManagerRef.current;
    if (!um) return;
    let mounted = true;
    const initialTransition = ++authTransitionRef.current;

    // PKCE return path: when the IdP redirects back with `?code=&state=`,
    // exchange the authorization code for tokens FIRST and seed the
    // userStore before getUser() runs. Without this, getUser() returns
    // null on every page load, AuthGate fires signIn() → IdP → callback
    // → AuthGate again → infinite loop. React StrictMode double-mounts
    // effects in dev; the authorization code is single-use, so the
    // second call would 400. Guard by sharing one in-flight exchange for
    // the same callback URL, then clear the params after success.
    const initialise = async (): Promise<void> => {
      try {
        await exchangeOidcCallbackOnce({ config: configRef.current, userManager: um });
        const user = await um.getUser();
        if (!mounted || authTransitionRef.current !== initialTransition) return;
        const nextStatus = await statusFromOidcUser(user);
        if (!mounted || authTransitionRef.current !== initialTransition) return;
        setActiveSession(nextStatus.state === 'authenticated' ? nextStatus.session : null);
        setStatus(nextStatus);
      } catch (error: unknown) {
        if (!mounted || authTransitionRef.current !== initialTransition) return;
        // If the callback exchange failed because the URL params were stale
        // (e.g. user reloaded mid-flow), clear them before showing the error
        // so a browser reload can restart the flow cleanly.
        clearOidcCallbackParams();
        setStatus({
          state: 'error',
          error: error instanceof Error ? error : new Error(String(error)),
        });
      }
    };

    void initialise();

    const onLoaded = (user: User) => {
      explicitlySignedOutRef.current = false;
      const transition = ++authTransitionRef.current;
      void statusFromOidcUser(user).then((nextStatus) => {
        if (!mounted || authTransitionRef.current !== transition) return;
        setActiveSession(nextStatus.state === 'authenticated' ? nextStatus.session : null);
        setStatus(nextStatus);
      });
    };
    const onUnloaded = () => {
      authTransitionRef.current += 1;
      setActiveSession(null);
      setStatus({
        state: 'unauthenticated',
        ...(explicitlySignedOutRef.current ? { reason: 'signed-out' as const } : {}),
      });
    };
    const onExpired = () => {
      authTransitionRef.current += 1;
      // The access token passed its `exp` and the background renew could not
      // refresh it. Without this handler the app stays `authenticated` with a
      // dead bearer, so every request 401s and the UI surfaces an error with no
      // path back to a login. Drop to `unauthenticated` so <AuthGate> restarts
      // the sign-in flow automatically. Tag it `reason: 'expired'` so AuthGate
      // prefers an SSO-friendly redirect (a full-page signinRedirect() silently
      // bounces through a still-valid Keycloak SSO cookie) instead of forcing
      // a full interactive prompt on a benign throttled-tab expiry.
      setActiveSession(null);
      setStatus({ state: 'unauthenticated', reason: 'expired' });
    };

    um.events.addUserLoaded(onLoaded);
    um.events.addUserUnloaded(onUnloaded);
    um.events.addAccessTokenExpired(onExpired);

    return () => {
      mounted = false;
      authTransitionRef.current += 1;
      um.events.removeUserLoaded(onLoaded);
      um.events.removeUserUnloaded(onUnloaded);
      um.events.removeAccessTokenExpired(onExpired);
    };
  }, [fakeSession]);

  const signIn = React.useCallback(
    async (options?: SignInOptions) => {
      if (fakeSession) {
        const session = normaliseFakeSession(fakeSession);
        if (explicitlySignedOutRef.current && !isExpired(session)) {
          explicitlySignedOutRef.current = false;
          authTransitionRef.current += 1;
          setActiveSession(session);
          setStatus({ state: 'authenticated', session });
          return;
        }
        if (!onFakeSessionSignIn) {
          throw new Error(
            'This build is using a baked local session and did not register a real sign-in handoff.',
          );
        }
        await onFakeSessionSignIn();
        return;
      }
      explicitlySignedOutRef.current = false;
      await userManagerRef.current?.signinRedirect({
        state: {
          kind: AUTH_RETURN_STATE_KIND,
          returnPath: getCurrentReturnPath(configRef.current.redirectUri),
        },
        ...(options?.prompt ? { prompt: options.prompt } : {}),
        ...(options?.maxAgeSeconds !== undefined ? { max_age: options.maxAgeSeconds } : {}),
      });
    },
    [fakeSession, onFakeSessionSignIn],
  );

  const signOut = React.useCallback(async () => {
    explicitlySignedOutRef.current = true;
    authTransitionRef.current += 1;
    setActiveSession(null);
    setStatus({ state: 'unauthenticated', reason: 'signed-out' });
    if (fakeSession) {
      return;
    }
    await userManagerRef.current?.signoutRedirect();
  }, [fakeSession]);

  const refresh = React.useCallback(async (): Promise<AuthSession | null> => {
    if (fakeSession) return normaliseFakeSession(fakeSession);
    const transition = ++authTransitionRef.current;
    const user = await userManagerRef.current?.signinSilent();
    const nextStatus = await statusFromOidcUser(user ?? null);
    if (authTransitionRef.current !== transition) return null;
    setActiveSession(nextStatus.state === 'authenticated' ? nextStatus.session : null);
    setStatus(nextStatus);
    return nextStatus.state === 'authenticated' ? nextStatus.session : null;
  }, [fakeSession]);

  const value = React.useMemo<AuthContextValue>(
    () => ({ status, signIn, signOut, refresh }),
    [status, signIn, signOut, refresh],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * Get the current auth context. Returns the full status union — UIs should
 * pattern-match `status.state` for safe rendering.
 */
export function useAuth(): AuthContextValue {
  const ctx = React.useContext(AuthContext);
  if (!ctx) throw new Error('useAuth() must be used inside <AuthProvider>');
  return ctx;
}

/** Shortcut: returns the active session or `null`. */
export function useSession(): HydratedAuthSession | null {
  const { status } = useAuth();
  return status.state === 'authenticated' ? status.session : null;
}

/**
 * Returns the deprecated tenant compatibility view (or `null` when
 * unauthenticated). It is derived from `/me` scope grants and contains no raw
 * JWT authorization claims. New app code should read `useSession().scopeGrants`.
 */
export function useTenantClaims(): HydratedAuthSession['cguardClaims'] | null {
  const session = useSession();
  return session?.cguardClaims ?? null;
}

function isExpired(session: AuthSession): boolean {
  return session.accessTokenExp <= Math.floor(Date.now() / 1000);
}

function fakeSessionExpiryDelayMs(session: AuthSession): number {
  return Math.max(0, session.accessTokenExp * 1000 - Date.now() - FAKE_SESSION_EXPIRY_SKEW_MS);
}

function normaliseFakeSession(session: AuthSession): HydratedAuthSession {
  return {
    ...session,
    userId: session.userId ?? session.cguardClaims.userId ?? '',
    tier: session.tier ?? null,
    sspId: session.sspId ?? null,
    ssuId: session.ssuId ?? null,
    siteId: session.siteId ?? null,
    roleNames: session.roleNames ?? session.roles,
    permissions: session.permissions ?? [],
    scopeGrants: session.scopeGrants ?? [],
    authzVersion: session.authzVersion ?? 0,
  };
}
